import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MOBILE_PAGE_SIZE = 10;

const hasDetailReturnMarker = (storageKey, filterKey) => {
    const markerKey = `${storageKey}:detail-return`;

    try {
        const marker = JSON.parse(sessionStorage.getItem(markerKey));
        const currentHistoryIndex = window.history.state?.idx;

        return marker?.filterKey === filterKey
            && marker?.historyIndex != null
            && currentHistoryIndex != null
            && marker.historyIndex === currentHistoryIndex;
    } catch (_) {
        return false;
    }
};

const readSavedState = (storageKey, filterKey) => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey));
        if (hasDetailReturnMarker(storageKey, filterKey) && saved?.filterKey === filterKey) {
            return {
                visibleCount: Math.max(MOBILE_PAGE_SIZE, Number(saved.visibleCount) || MOBILE_PAGE_SIZE),
                scrollY: Math.max(0, Number(saved.scrollY) || 0),
            };
        }
    } catch (_) {
        // A corrupt or unavailable session store should never block the list.
    }

    return { visibleCount: MOBILE_PAGE_SIZE, scrollY: 0 };
};

const writeSavedState = (storageKey, filterKey, visibleCount, scrollY) => {
    try {
        const previous = JSON.parse(sessionStorage.getItem(storageKey));
        const savedScrollY = scrollY ?? (previous?.filterKey === filterKey ? previous.scrollY : 0);
        sessionStorage.setItem(storageKey, JSON.stringify({ filterKey, visibleCount, scrollY: savedScrollY }));
    } catch (_) {
        // Session persistence is a progressive enhancement.
    }
};

const useMobileLoadMore = ({ items, filterKey, storageKey, isMobile }) => {
    const initialStateRef = useRef(null);
    if (!initialStateRef.current) {
        initialStateRef.current = readSavedState(storageKey, filterKey);
    }

    const [visibleCount, setVisibleCount] = useState(initialStateRef.current.visibleCount);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const previousFilterKeyRef = useRef(filterKey);
    const restoredScrollRef = useRef(false);

    useEffect(() => {
        try {
            sessionStorage.removeItem(`${storageKey}:detail-return`);
        } catch (_) {
            // Session persistence is a progressive enhancement.
        }
    }, [storageKey]);

    const uniqueItems = useMemo(() => {
        const ids = new Set();
        return items.filter((item) => {
            const id = String(item?._id ?? item?.id ?? '');
            if (!id || ids.has(id)) return false;
            ids.add(id);
            return true;
        });
    }, [items]);

    useEffect(() => {
        if (previousFilterKeyRef.current === filterKey) return;
        previousFilterKeyRef.current = filterKey;
        restoredScrollRef.current = true;
        setIsLoadingMore(false);
        setVisibleCount(MOBILE_PAGE_SIZE);
        writeSavedState(storageKey, filterKey, MOBILE_PAGE_SIZE, 0);
    }, [filterKey, storageKey]);

    useEffect(() => {
        if (!isMobile || restoredScrollRef.current || uniqueItems.length === 0) return;
        restoredScrollRef.current = true;
        const savedScrollY = initialStateRef.current.scrollY;
        if (savedScrollY > 0) {
            requestAnimationFrame(() => window.scrollTo({ top: savedScrollY, behavior: 'auto' }));
        }
    }, [isMobile, uniqueItems.length]);

    useEffect(() => {
        if (!isMobile) return;
        writeSavedState(storageKey, filterKey, visibleCount);
    }, [filterKey, isMobile, storageKey, visibleCount]);

    const loadMore = useCallback(() => {
        if (isLoadingMore || visibleCount >= uniqueItems.length) return;
        setIsLoadingMore(true);
        requestAnimationFrame(() => {
            setVisibleCount((count) => Math.min(count + MOBILE_PAGE_SIZE, uniqueItems.length));
            setIsLoadingMore(false);
        });
    }, [isLoadingMore, uniqueItems.length, visibleCount]);

    const rememberScrollPosition = useCallback(() => {
        writeSavedState(storageKey, filterKey, visibleCount, window.scrollY);
        try {
            sessionStorage.setItem(`${storageKey}:detail-return`, JSON.stringify({
                filterKey,
                historyIndex: window.history.state?.idx,
            }));
        } catch (_) {
            // Session persistence is a progressive enhancement.
        }
    }, [filterKey, storageKey, visibleCount]);

    return {
        mobileItems: uniqueItems.slice(0, visibleCount),
        visibleCount: Math.min(visibleCount, uniqueItems.length),
        totalCount: uniqueItems.length,
        hasMore: visibleCount < uniqueItems.length,
        isLoadingMore,
        loadMore,
        rememberScrollPosition,
    };
};

export default useMobileLoadMore;
